import uploadFileToS3 from '@components/RunOfShow/helper/uploadToS3';
import { Slider } from '@heroui/react';
import React, { useState } from 'react';
import FileUploader from '@molecules/FileUploader';
import ButtonNew from '@atoms/Button.new';
import { type InteractionWithoutPDF } from '@components/Presentation/types';
import { useSelectedSlide } from '@components/Presentation/hooks/useSelectedSlide';
import { usePresentationStore } from '@components/Presentation/store/presentationStore';
import { type ThemeConfig } from '@components/themes/store/ThemeStore';
import useGetImageProperties from '@components/Presentation/hooks/useGetImageProperties';
import ErrorBanner from '@components/themes/ThemeModal/EditTheme/MenuSettings/units/ErrorBanner';
import ImageLoader from '@atoms/new/imageLoader';
import { PlusIcon as AddRounded, XIcon as CloseRoundedIcon } from '@phosphor-icons/react';

const interactionImgKey: Record<InteractionWithoutPDF, keyof ThemeConfig['images']> = {
  'Quick Questions': 'quickQuestionImageSrc',
  emojisEverywhere: 'emojisEverywhereImageSrc',
  fantasticFans: 'ffImageSrc',
  freeForm: 'pollImageSrc',
  linkLibrary: 'linkLibraryImageSrc',
  map: 'mapImageSrc',
  newWheel: 'winningWheelImageSrc',
  pollWithOptions: 'pollWithOptionsImageSrc',
  ratingPolls: 'ratingPollsImageSrc',
  textTrack: 'textTrackImageSrc',
  transientThought: 'ttImageSrc',
  wheel: 'choiceCircleImageSrc',
  wordCloud: 'wcImageSrc',
  talkingTiles: 'talkingTilesImageSrc',
  multipleChoice: 'multipleChoiceImageSrc',
  quiz: 'quizImageSrc',
  leaderboard: 'leaderboardImageSrc',
  credits: 'creditsImageSrc',
};

const interactionImgOpacityKey: Record<InteractionWithoutPDF, keyof ThemeConfig['images']> = {
  'Quick Questions': 'quickQuestionImageOpacity',
  emojisEverywhere: 'emojisEverywhereImageOpacity',
  fantasticFans: 'ffImageOpacity',
  freeForm: 'pollImageOpacity',
  linkLibrary: 'linkLibraryImageOpacity',
  map: 'mapImageOpacity',
  newWheel: 'winningWheelImageOpacity',
  pollWithOptions: 'pollWithOptionsImageOpacity',
  ratingPolls: 'ratingPollsImageOpacity',
  textTrack: 'textTrackImageOpacity',
  transientThought: 'ttImageOpacity',
  wheel: 'choiceCircleImageOpacity',
  wordCloud: 'wcImageOpacity',
  talkingTiles: 'talkingTilesImageOpacity',
  multipleChoice: 'multipleChoiceImageOpacity',
  quiz: 'quizImageOpacity',
  leaderboard: 'leaderboardImageOpacity',
  credits: 'creditsImageOpacity',
};

const isVideoFile = (src: string | null | undefined): boolean => {
  if (!src) return false;
  const extension = src.split('.').pop()?.toLowerCase();
  return extension === 'mp4' || extension === 'webm';
};

export default function BackgroundImage() {
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [customErrMessage, setCustomErrMessage] = useState('');
  const selectedSlide = useSelectedSlide();
  const selectedTheme = usePresentationStore((s) => s._ext?.selectedTheme);

  const { getImageProperties } = useGetImageProperties(selectedTheme?.config.default);

  const interaction = getImageProperties(selectedSlide.type as InteractionWithoutPDF, selectedSlide.setting);
  const isVideo = isVideoFile(interaction.src);

  const uploadFileToS3andSave = async (fileToUpload: any) => {
    setIsLoading(true);
    try {
      const { name } = fileToUpload[0];
      const extension = name.split('.').slice(-1)[0];
      const fileNameWithoutExtension = name.replace(`.${extension}`, '').replace(/\s+/g, '_');
      const fileKey = `Theme-Images/${fileNameWithoutExtension}_${Date.now()}.${extension}`;

      await uploadFileToS3(fileToUpload[0], fileKey, setUploadPercentage);

      selectedSlide.handler.updateSetting(interactionImgKey[selectedSlide.type as InteractionWithoutPDF], fileKey);
    } catch (error) {
      setUploadPercentage(0);
    }
    setIsLoading(false);
  };

  const handleDeleteFileFromS3 = async () => {
    if (!interaction.src) return;
    selectedSlide.handler.updateSetting(interactionImgKey[selectedSlide.type as InteractionWithoutPDF], null);
    selectedSlide.handler.updateSetting(interactionImgOpacityKey[selectedSlide.type as InteractionWithoutPDF], 100);
  };

  const updateImageOpacity = (val: number) => {
    selectedSlide.handler.updateSetting(interactionImgOpacityKey[selectedSlide.type as InteractionWithoutPDF], val);
  };

  return (
    <div className='flex flex-col gap-4'>
      <ErrorBanner errMsg={customErrMessage} />
      <div className='flex justify-between items-center'>
        <p className='flex items-center gap-2 text-sm'>
          <span>Image or video</span>
        </p>
        {interaction.src && <div className='relative h-8 w-14 shadow-md'>
          {isVideo ? (
            <video
              src={`${process.env.NEXT_PUBLIC_BUCKET_URL}${interaction.src}`}
              className='w-14 h-8 rounded-md object-cover bg-linear-to-r from-grey-0 to-grey-50'
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
            />
          ) : (
            <ImageLoader
              src={`${process.env.NEXT_PUBLIC_BUCKET_URL}${interaction.src}`}
              className='w-14 h-8 rounded-md object-cover bg-linear-to-r from-grey-0 to-grey-50'
              alt='background image'
              width={720}
              height={360}
            />
          )}
          <button
            onClick={handleDeleteFileFromS3}
            className='w-6 rounded-full absolute -top-6 translate-y-1/2 right-0 bg-grey-100 dark:bg-grey-800 translate-x-1/2 shadow-md hover:bg-grey-200 hover:dark:bg-grey-700 transition-colors'>
            <CloseRoundedIcon size={20} className='!text-default-400' />
          </button>
        </div>}
        {!interaction.src && <FileUploader
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/webp': ['.webp'],
            // @ts-expect-error - video types supported at runtime
            'video/mp4': ['.mp4'],
            'video/webm': ['.webm']
          }}
          element={
            <ButtonNew
              addonLeft={<AddRounded size={20} />}
              variant='secondary'
              isLoading={isLoading}
              disabled={isLoading}
              spinnerColor='default'
              size='md'>
              Add
            </ButtonNew>
          }
          uploadFileToS3andSave={uploadFileToS3andSave}
          showProgress={false}
          maxSize={31457280}
          maxSizeErrorMsg='Maximum size allowed is 30MB.'
          isUploadInProgress={false}
          setIsUploadInProgress={() => { }}
          updateCustomErrorMessage={(msg: string) => setCustomErrMessage(msg)}
          showDefaultErrorMessage={false}
        />}

      </div>
      {interaction.src && <div className='flex justify-between items-center text-sm'>
        <p>{isVideo ? 'Video' : 'Image'} transparency ({interaction.opacity}%)</p>
        <Slider
          color='danger'
          step={1}
          maxValue={100}
          minValue={0}
          defaultValue={100}
          value={interaction.opacity}
          onChange={(val) => updateImageOpacity(Math.floor(val as number))}
          aria-label="transparency"
          classNames={{
            base: 'max-w-[140px] cursor-pointer gap-3',
            track: 'slider-border-width h-1',
            filler: 'bg-red-500'
          }}
          renderThumb={(props) => (
            <div
              {...props}
              className="group p-1 top-1/2 bg-red-500 shadow-medium rounded-full cursor-grab data-[dragging=true]:cursor-grabbing"
            >
              <span className="bg-red-500 shadow-small rounded-full w-2 h-2 block" />
            </div>
          )}
        />
      </div>}
    </div>
  );
}
